'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useId, useRef, useState, } from 'react';
import { cx } from '../../../internal/cx.js';
import { CheckGlyph, InfoGlyph, WarningGlyph } from '../../../internal/icons.js';
import { useFieldControl } from '../../forms/form-field/index.js';
import { Spinner } from '../../feedback/spinner/index.js';
/**
 * Runs `validate` for `value` after `debounce` ms without changes. Earlier
 * checks are aborted and their late results ignored, so the state always
 * describes the latest value. Empty values are `idle`.
 */
export function useAsyncValidation(value, validate, { debounce = 400 } = {}) {
    const [state, setState] = useState({
        status: 'idle',
    });
    const [nonce, setNonce] = useState(0);
    const immediate = useRef(false);
    const validateRef = useRef(validate);
    validateRef.current = validate;
    useEffect(() => {
        if (value === '') {
            setState({ status: 'idle' });
            return;
        }
        const controller = new AbortController();
        const run = async () => {
            try {
                const result = await validateRef.current(value, controller.signal);
                if (controller.signal.aborted)
                    return;
                if (result === null)
                    setState({ status: 'idle' });
                else
                    setState(result.message === undefined
                        ? { status: result.valid ? 'valid' : 'invalid' }
                        : { status: result.valid ? 'valid' : 'invalid', message: result.message });
            }
            catch {
                if (!controller.signal.aborted)
                    setState({ status: 'error' });
            }
        };
        setState({ status: 'checking' });
        const wait = immediate.current ? 0 : debounce;
        immediate.current = false;
        const timer = setTimeout(() => void run(), wait);
        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [value, debounce, nonce]);
    const revalidate = useCallback(() => {
        immediate.current = true;
        setNonce((n) => n + 1);
    }, []);
    return { status: state.status, message: state.message, revalidate };
}
const defaultMessages = {
    checking: 'Checking availability…',
    valid: 'Available',
    invalid: 'Not available',
    error: 'Couldn’t check availability.',
    retry: 'Try again',
};
const statusIcon = {
    idle: null,
    checking: _jsx(Spinner, { size: "sm" }),
    valid: _jsx(CheckGlyph, {}),
    invalid: _jsx(WarningGlyph, {}),
    error: _jsx(InfoGlyph, {}),
};
/**
 * A text input whose value is checked asynchronously while the user types:
 * handles, workspace addresses, slugs. The product supplies the check; the
 * component debounces it, aborts stale requests, shows a status icon with a
 * text message, and announces the result to screen readers.
 */
export const UsernameAvailabilityInput = forwardRef(function UsernameAvailabilityInput({ value: valueProp, defaultValue = '', onValueChange, validate, debounce = 400, transform, prefix, onStatusChange, messages: messagesProp, size = 'md', invalid, className, ...rest }, ref) {
    const id = useId();
    const prefixId = `mn-uai-${id}-prefix`;
    const messageId = `mn-uai-${id}-message`;
    const messages = { ...defaultMessages, ...messagesProp };
    const [internal, setInternal] = useState(defaultValue);
    const value = valueProp !== undefined ? valueProp : internal;
    const { status, message, revalidate } = useAsyncValidation(value, validate, { debounce });
    const text = status === 'idle'
        ? ''
        : status === 'checking'
            ? messages.checking
            : (message ?? messages[status]);
    // Announce settled results only: "checking" on every pause would be noise.
    const announcement = status === 'valid' || status === 'invalid' || status === 'error' ? text : '';
    const statusRef = useRef(onStatusChange);
    statusRef.current = onStatusChange;
    useEffect(() => {
        statusRef.current?.(status, message);
    }, [status, message]);
    const fieldProps = useFieldControl({
        ...rest,
        'aria-describedby': cx(prefix ? prefixId : undefined, rest['aria-describedby'], text ? messageId : undefined),
    }, { invalid: invalid || (status === 'invalid' ? true : undefined) });
    return (_jsxs("div", { className: cx('mn-username-availability-input', className), "data-status": status, "data-size": size, children: [_jsxs("div", { className: "mn-input mn-username-availability-input__control", "data-size": size, "data-disabled": fieldProps.disabled || undefined, "data-invalid": fieldProps['aria-invalid'] ? true : undefined, children: [prefix ? (_jsx("span", { id: prefixId, className: "mn-username-availability-input__prefix", children: prefix })) : null, _jsx("input", { ref: ref, autoComplete: "off", autoCapitalize: "none", autoCorrect: "off", spellCheck: false, ...fieldProps, type: "text", className: "mn-username-availability-input__input", value: value, onChange: (e) => {
                            const next = transform ? transform(e.target.value) : e.target.value;
                            if (valueProp === undefined)
                                setInternal(next);
                            onValueChange?.(next);
                        } }), statusIcon[status] ? (_jsx("span", { className: "mn-username-availability-input__icon", "aria-hidden": true, children: statusIcon[status] })) : null] }), text ? (_jsxs("p", { id: messageId, className: "mn-username-availability-input__message", children: [_jsx("span", { children: text }), status === 'error' ? (_jsx("button", { type: "button", className: "mn-username-availability-input__retry", onClick: revalidate, children: messages.retry })) : null] })) : null, _jsx("span", { className: "mn-visually-hidden", role: "status", children: announcement })] }));
});
//# sourceMappingURL=username-availability-input.js.map